import { LocalDate } from "@js-joda/core";
import { useEffect, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { SegmentController } from "components/designV2/segmentcontroller/segmentcontroller";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import {
  Container,
  Header,
  Title,
  Controls,
  DateNav,
  DateLabel,
  QuickButtons,
  DatePickerWrapper,
  CardsGrid,
  DragHandle,
  StatCard,
  CardLabel,
  CardValue,
  CardLoader,
  ManageLink,
} from "./analytics.component.styles";
import { Api } from "services/api/api.service";
import { type GetTimeReportsV2SummaryResponse } from "services/api/company/timeReports.service";
import { formatIsoDate } from "web-apps/company/components/schedule/utils/schedule.dates";
import { getCostsTimeReports } from "web-apps/company/utils/utils";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { type Order } from "model/Analytics";
import { type TimeReportDto } from "model/TimeReport";
import { AnalyticsChart } from "./analytics.chart.component";
import {
  type CustomCost,
  loadCustomCosts,
  getCustomCostsTotal,
  CustomCostsPane,
} from "./analytics.customCosts";
import {
  type CustomEarning,
  loadCustomEarnings,
  getCustomEarningsTotal,
  CustomEarningsPane,
} from "./analytics.customEarnings";
import {
  type AnalyticsPeriod,
  type OperatingHours,
  loadOperatingHours,
  hasStoredOperatingHours,
  isOrderInBusinessDay,
  getBusinessDate,
  parseTimestamp,
  OperatingHoursPane,
} from "./analytics.operatingHours";
import {
  computeBusinessHourGross,
  CARD_ORDER_STORAGE_KEY,
  initCardOrder,
  buildStatCards,
  sortStatCards,
  reorderCards,
  formatDateLabel,
  getDateRange,
  toYearMonth,
  isOvernightActive,
} from "./analytics.component.logic";

export const Analytics = () => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();
  const companyId = companyState.company?.id;

  const [period, setPeriod] = useState<AnalyticsPeriod>("month");
  const [selectedDate, setSelectedDate] = useState<LocalDate>(LocalDate.now());
  const [summary, setSummary] =
    useState<GetTimeReportsV2SummaryResponse | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [personnelCost, setPersonnelCost] = useState<number | null>(null);
  const [personnelCostLoading, setPersonnelCostLoading] = useState(false);
  const [sales, setSales] = useState<number | null>(null);
  const [tips, setTips] = useState<number | null>(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [rawOrders, setRawOrders] = useState<Order[]>([]);
  const [rawTimeReports, setRawTimeReports] = useState<TimeReportDto[]>([]);
  const [cardOrder, setCardOrder] = useState<string[]>(initCardOrder);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropSlot, setDropSlot] = useState<number | null>(null);
  const [operatingHours, setOperatingHours] =
    useState<OperatingHours>(loadOperatingHours);
  const [editingHours, setEditingHours] = useState(
    () => !hasStoredOperatingHours(),
  );
  const [customCosts, setCustomCosts] = useState<CustomCost[]>(loadCustomCosts);
  const [editingCustomCosts, setEditingCustomCosts] = useState(false);
  const [customEarnings, setCustomEarnings] =
    useState<CustomEarning[]>(loadCustomEarnings);
  const [editingCustomEarnings, setEditingCustomEarnings] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    setSummary(null);
    setSummaryLoading(true);
    const { fromDate, toDate } = getDateRange(selectedDate, period);
    Api()
      .company.timeReports.getTimeReportsV2Summary(
        companyId,
        formatIsoDate(fromDate),
        formatIsoDate(toDate),
      )
      .then((res) => {
        setSummary(res.data);
        setSummaryLoading(false);
      })
      .catch(() => {
        setSummary(null);
        setSummaryLoading(false);
      });
  }, [companyId, selectedDate, period]);

  useEffect(() => {
    if (!companyId) return;
    setPersonnelCost(null);
    setPersonnelCostLoading(true);
    setRawTimeReports([]);
    const { fromDate, toDate } = getDateRange(selectedDate, period);
    const apiToDate = isOvernightActive(period, selectedDate, operatingHours)
      ? toDate.plusDays(1)
      : toDate;
    // Build the complete set of year-months covered by this period so we
    // fetch time reports from all relevant months (critical for year view).
    const yearMonths = new Set<number>();
    let ymCursor = fromDate.withDayOfMonth(1);
    while (!ymCursor.isAfter(apiToDate)) {
      yearMonths.add(toYearMonth(ymCursor));
      ymCursor = ymCursor.plusMonths(1);
    }

    Promise.all(
      Array.from(yearMonths).map((ym) =>
        Api().company.timeReport.getAllTimeReports(companyId, ym),
      ),
    )
      .then(async (responses) => {
        const allReports = responses.flatMap((res) =>
          res.data.flatMap((w) => w.timeReports),
        );

        const filtered = allReports.filter((r) => {
          if (!r.startTime) return false;
          const reportDate = LocalDate.parse(r.startTime.substring(0, 10));
          // Use apiToDate so reports that start on the extra calendar
          // day fetched for overnight windows (e.g. June 1 01:30 belonging to
          // May 31's business window) are not dropped here.
          return (
            !reportDate.isBefore(fromDate) && !reportDate.isAfter(apiToDate)
          );
        });

        setRawTimeReports(filtered);

        if (filtered.length === 0) {
          setPersonnelCost(0);
          setPersonnelCostLoading(false);
          return;
        }

        const costs = await getCostsTimeReports(filtered, companyId);
        const totalCost =
          typeof costs?.totalCost === "number" ? costs.totalCost : null;
        const grossSalary =
          typeof costs?.grossSalary === "number" ? costs.grossSalary : 0;

        if (totalCost !== null && grossSalary > 0) {
          // Scale the business-hour gross by the same ratio the API uses to
          // convert gross salary to total cost (employer contributions etc.).
          const multiplier = totalCost / grossSalary;
          const businessGross = computeBusinessHourGross(
            filtered,
            period,
            selectedDate,
            operatingHours,
          );
          setPersonnelCost(businessGross * multiplier);
        } else {
          setPersonnelCost(totalCost);
        }
        setPersonnelCostLoading(false);
      })
      .catch(() => {
        setRawTimeReports([]);
        setPersonnelCost(null);
        setPersonnelCostLoading(false);
      });
  }, [companyId, selectedDate, period, operatingHours]);

  useEffect(() => {
    if (!companyId) return;
    setSales(null);
    setTips(null);
    setRawOrders([]);
    setOrdersLoading(true);
    const { fromDate, toDate } = getDateRange(selectedDate, period);
    const apiToDate = isOvernightActive(period, selectedDate, operatingHours)
      ? toDate.plusDays(1)
      : toDate;
    Api()
      .analytics.getOrders(companyId, fromDate.toString(), apiToDate.toString())
      .then((res) => {
        const filtered = res.data.filter((o) => {
          const ts = parseTimestamp(o.timestamp);
          if (period === "day") {
            return isOrderInBusinessDay(ts, selectedDate, operatingHours);
          }
          // week / month / year: verify the order's business date falls within
          // the period. isOrderInBusinessWindow alone is insufficient — a
          // post-midnight order on the first calendar day of the period can
          // belong to the previous period's overnight window and must be excluded.
          const bizDate = getBusinessDate(ts, operatingHours);
          return (
            bizDate !== null &&
            !bizDate.isBefore(fromDate) &&
            !bizDate.isAfter(toDate)
          );
        });
        setRawOrders(filtered);
        setSales(filtered.reduce((sum, o) => sum + o.amount, 0));
        setTips(filtered.reduce((sum, o) => sum + o.tip, 0));
        setOrdersLoading(false);
      })
      .catch(() => {
        setRawOrders([]);
        setSales(null);
        setTips(null);
        setOrdersLoading(false);
      });
  }, [companyId, selectedDate, period, operatingHours]);

  const customCostsTotal = getCustomCostsTotal(
    customCosts,
    period,
    selectedDate,
  );
  const customEarningsTotal = getCustomEarningsTotal(
    customEarnings,
    period,
    selectedDate,
  );

  const orderedStats = sortStatCards(
    buildStatCards({
      labels: {
        sales: t("CompanyAnalytics.Sales"),
        customEarnings: t("CompanyAnalytics.CustomEarnings"),
        personnelCost: t("CompanyAnalytics.PersonnelCost"),
        customCosts: t("CompanyAnalytics.CustomCosts"),
        personnelCostPct: t("CompanyAnalytics.PersonnelCostPct"),
        plannedHours: t("CompanyAnalytics.PlannedHours"),
        salesPerHour: t("CompanyAnalytics.SalesPerHour"),
        tips: t("CompanyAnalytics.Tips"),
        result: t("CompanyAnalytics.Result"),
      },
      sales,
      tips,
      personnelCost,
      customEarningsTotal,
      customCostsTotal,
      summary,
      operatingHours,
      period,
      selectedDate,
      ordersLoading,
      personnelCostLoading,
      summaryLoading,
    }),
    cardOrder,
  );

  const handleDragStart = (index: number) => setDragIndex(index);

  const handleDragOver = (e: React.DragEvent<HTMLElement>, index: number) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const slot = e.clientX < rect.left + rect.width / 2 ? index : index + 1;
    if (dropSlot !== slot) setDropSlot(slot);
  };

  const handleGridDragLeave = (e: React.DragEvent<HTMLElement>) => {
    const related = e.relatedTarget instanceof Node ? e.relatedTarget : null;
    if (!e.currentTarget.contains(related)) {
      setDropSlot(null);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (dragIndex === null || dropSlot === null) {
      setDragIndex(null);
      setDropSlot(null);
      return;
    }
    const newOrder = reorderCards(
      orderedStats.map((s) => s.key),
      dragIndex,
      dropSlot,
    );
    setCardOrder(newOrder);
    localStorage.setItem(CARD_ORDER_STORAGE_KEY, JSON.stringify(newOrder));
    setDragIndex(null);
    setDropSlot(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDropSlot(null);
  };

  const handlePrev = () => {
    if (period === "month") setSelectedDate((d) => d.minusMonths(1));
    else if (period === "year") setSelectedDate((d) => d.minusYears(1));
    else setSelectedDate((d) => d.minusDays(period === "week" ? 7 : 1));
  };
  const handleNext = () => {
    if (period === "month") setSelectedDate((d) => d.plusMonths(1));
    else if (period === "year") setSelectedDate((d) => d.plusYears(1));
    else setSelectedDate((d) => d.plusDays(period === "week" ? 7 : 1));
  };

  const periodOptions = [
    { value: "day", label: t("CompanyAnalytics.Day") },
    { value: "week", label: t("CompanyAnalytics.Week") },
    { value: "month", label: t("CompanyAnalytics.Month") },
    { value: "year", label: t("CompanyAnalytics.Year") },
  ];

  return (
    <Container>
      <Header>
        <Title>{t("CompanyAnalytics.Analytics")}</Title>
        <Controls>
          <SegmentController
            options={periodOptions}
            value={period}
            onChange={(v) => {
              if (v === "day" || v === "week" || v === "month" || v === "year")
                setPeriod(v);
            }}
          />
          <DateNav>
            <SecondaryButton
              iconName="v2/left-chevron"
              size="small"
              onClick={handlePrev}
            />
            <DateLabel>
              {formatDateLabel(
                selectedDate,
                period,
                t("CompanyAnalytics.Today"),
                t("CompanyAnalytics.Yesterday"),
              )}
            </DateLabel>
            <SecondaryButton
              iconName="v2/right-chevron"
              size="small"
              onClick={handleNext}
            />
          </DateNav>
          <QuickButtons>
            <SecondaryButton
              label={t("CompanyAnalytics.Today")}
              size="small"
              onClick={() => setSelectedDate(LocalDate.now())}
            />
            <SecondaryButton
              label={t("CompanyAnalytics.Yesterday")}
              size="small"
              onClick={() => setSelectedDate(LocalDate.now().minusDays(1))}
            />
            <DatePickerWrapper>
              <DatePicker
                value={selectedDate}
                onChange={(date) => {
                  if (date) setSelectedDate(date);
                }}
                size="compact"
                dateFormat="DD/MM/YYYY"
              />
            </DatePickerWrapper>
          </QuickButtons>
        </Controls>
      </Header>

      <CardsGrid
        onDragLeave={handleGridDragLeave}
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        {orderedStats.map((stat, index) => (
          <StatCard
            key={stat.label}
            draggable
            $isDragging={dragIndex === index}
            $dropIndicator={
              dropSlot === index
                ? "left"
                : dropSlot === index + 1
                  ? "right"
                  : null
            }
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
          >
            <DragHandle />
            <CardLabel>{stat.label}</CardLabel>
            {stat.isLoading ? (
              <CardLoader>
                <CircularProgress size={24} />
              </CardLoader>
            ) : (
              <CardValue>{stat.value}</CardValue>
            )}
            {stat.key === "customCosts" && (
              <ManageLink onClick={() => setEditingCustomCosts(true)}>
                {t("CompanyAnalytics.ManageCustomCosts")} →
              </ManageLink>
            )}
            {stat.key === "customEarnings" && (
              <ManageLink onClick={() => setEditingCustomEarnings(true)}>
                {t("CompanyAnalytics.ManageCustomEarnings")} →
              </ManageLink>
            )}
          </StatCard>
        ))}
      </CardsGrid>

      <AnalyticsChart
        orders={rawOrders}
        timeReports={rawTimeReports}
        period={period}
        selectedDate={selectedDate}
        operatingHours={operatingHours}
        totalPersonnelCost={personnelCost}
        customCosts={customCosts}
        customEarnings={customEarnings}
        loading={ordersLoading || personnelCostLoading}
        onEditHours={() => setEditingHours(true)}
      />

      <OperatingHoursPane
        initial={operatingHours}
        open={editingHours}
        onSave={(hours) => {
          setOperatingHours(hours);
          setEditingHours(false);
        }}
        onClose={() => setEditingHours(false)}
      />
      <CustomCostsPane
        initial={customCosts}
        open={editingCustomCosts}
        selectedDate={selectedDate}
        onSave={(costs) => {
          setCustomCosts(costs);
          setEditingCustomCosts(false);
        }}
        onClose={() => setEditingCustomCosts(false)}
      />
      <CustomEarningsPane
        initial={customEarnings}
        open={editingCustomEarnings}
        selectedDate={selectedDate}
        onSave={(earnings) => {
          setCustomEarnings(earnings);
          setEditingCustomEarnings(false);
        }}
        onClose={() => setEditingCustomEarnings(false)}
      />
    </Container>
  );
};
